import { type MouseEvent, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { SORT_BY } from '@eshop/type'
import { useQuery } from '@tanstack/react-query'

import { cloneDeep } from 'lodash'

import css from './Eshop.module.css'

import { fetchEshopFilters } from '#/local/repository/Filters'
import { fetchProductCodesByFilters, fetchProductCodesByQuery, fetchProductsEshop } from '#/local/repository/Product'
import { sendReport } from '#/local/repository/Report'

import { AddToCartEshop } from '#/global/component/Product/AddToCart/Eshop'
import { Availability } from '#/global/component/Product/Availability'
import { ProductDiscount } from '#/global/component/Product/Discount'
import { LayoutGrid } from '#/global/component/Product/LayoutGrid'
import { PriceEshopPreview } from '#/global/component/Product/Price/Eshop'
import { type FiltersSelectedValues, ProductFilters } from '#/global/component/Product/ProductFilters'
import { SelectedFilters } from '#/global/component/Product/ProductFilters/SelectedFilters'
import { ProductIcon } from '#/global/component/Product/ProductIcon'
import { ProductLabel } from '#/global/component/Product/ProductLabel'
import { ProductPreviewLong } from '#/global/component/Product/ProductPreviewLong'
import { Sticker } from '#/global/component/Product/Sticker'

import { Config } from '#/global/helper/Config'
import { linkToProductEshop } from '#/global/helper/LinkTo'
import { MessageFlash } from '#/global/helper/MessageFlash'

export const Eshop = () => {
    const lang = Config.getLang()
    const [searchParameters, setSearchParameters] = useSearchParams()
    const [filtersValues, setFiltersValues] = useState<FiltersSelectedValues<'AVAILABILITY' | 'BRAND' | 'PRICE'>>({ PRICE: [], AVAILABILITY: [], BRAND: [] })

    const query = searchParameters.get('query')
    const page = Number(searchParameters.get('page')) || 1
    const sortBy = (searchParameters.get('sortBy') as SORT_BY) ?? 'DEFAULT'

    // Fetch original product codes (All product codes without filtering. Max 240 items.)
    const productCodesOrigin = useQuery({
        queryKey: ['eshop productCodesOrigin', { query }],
        queryFn: async () => fetchProductCodesByQuery(query!, 'eshop'),
        enabled: !!query,
    })

    /**
     * Fetch original filters
     */
    const filtersOrigin = useQuery({
        queryKey: ['eshop filters', { productCodesOrigin: productCodesOrigin.data }],
        queryFn: async () => fetchEshopFilters(productCodesOrigin.data!, {}),
        enabled: productCodesOrigin.isSuccess,
    })

    /**
     * Fetch filtered product codes
     */
    const productCodesFiltered = useQuery({
        queryKey: ['eshop productCodesFiltered', { filtersValues, sortBy, productCodesOrigin: productCodesOrigin.data }],
        queryFn: async () => fetchProductCodesByFilters(productCodesOrigin.data!, filtersValues, sortBy, 'eshop'),
        enabled: productCodesOrigin.isSuccess,
    })

    /**
     * Fetch filtered filters
     */
    const filtersFiltered = useQuery({
        queryKey: ['eshop filtersFiltered', { filtersValues, productCodesOrigin: productCodesOrigin.data }],
        queryFn: async () =>
            fetchEshopFilters(productCodesOrigin.data!, filtersValues).then((filters) => {
                const filtersFiltered = cloneDeep(filtersOrigin.data)?.map((origin) => {
                    const filter = filters.find(filter => filter.id === origin.id) // match filtru z api s originálním filtrem
                    origin.values = origin.values.map((v) => { // procházení hodnot originálního filtru
                        if (origin.id === 'PRICE') {
                            const f = filter?.values.find(f => f.title === v.title) // match hodnoty z api s originální hodnotou
                            v.count = f?.count ?? 0 // set count
                            v.value = f?.value ?? v.value // set value
                            return v
                        }
                        const f = filter?.values.find(f => f.value === v.value) // match hodnoty z api s originální hodnotou
                        v.count = f?.count ?? 0 // set count
                        return v
                    })
                    return origin
                }) ?? []

                return filtersFiltered
            }),
        enabled: productCodesFiltered.isSuccess && filtersOrigin.isSuccess,
    })

    /**
     * Fetch products by page
     */
    const products = useQuery({
        queryKey: ['eshop products', { productCodesFiltered: productCodesFiltered.data, page }],
        queryFn: async () => fetchProductsEshop(productCodesFiltered.data!, page),
        placeholderData: previousData => previousData ?? (Array.from({ length: 12 })),
        enabled: productCodesFiltered.isSuccess,
    })

    // Page change
    const setPage = (page: number) => {
        const urlParameters = new URLSearchParams(window.location.search)
        urlParameters.set('page', page.toString())
        setSearchParameters(urlParameters)
    }

    // Sort change
    const setSortBy = (sortBy: SORT_BY) => {
        const urlParameters = new URLSearchParams(window.location.search)
        urlParameters.set('sortBy', sortBy.toString())
        setSearchParameters(urlParameters)
    }

    // Filter change
    const handleFilterChange = (filterId: string, selected: string[] | number[]) => {
        if (filterId === 'ALL') {
            setFiltersValues({ AVAILABILITY: [], BRAND: [], PRICE: [] })
        } else {
            setFiltersValues({ ...filtersValues, [filterId]: selected })
        }
        setPage(1)
    }

    if (productCodesOrigin.isError || filtersOrigin.isError || productCodesFiltered.isError || filtersFiltered.isError || products.isError) {
        MessageFlash.add({
            message: 'Nastala chyba při načítání produktů.',
            type: 'error',
        })
    }

    const handleSendReport = (event: MouseEvent<HTMLAnchorElement>, { code, slug }: { code: string, slug: string }) => {
        event.preventDefault()
        const link = linkToProductEshop(code, slug)
        query && void sendReport(query, 'products-eshop', code, link)
    }

    return (
        <div className={css.container}>
            {filtersFiltered.isSuccess && filtersOrigin.isSuccess
                ? (
                        <ProductFilters
                            filters={filtersFiltered.data}
                            handleChange={handleFilterChange}
                            lang={lang}
                            values={filtersValues}
                        />
                    )
                : <ProductFilters loadingState />}
            <div>
                {filtersFiltered.isSuccess && (
                    <SelectedFilters
                        filters={filtersFiltered.data}
                        handleChange={handleFilterChange}
                        lang={lang}
                        values={filtersValues}
                    />
                )}
                <LayoutGrid
                    lang={lang}
                    loadingState={!products.isSuccess || !productCodesFiltered.isSuccess}
                    page={page}
                    productsCount={productCodesFiltered.data?.length ?? 0}
                    setPage={setPage}
                    setSortBy={setSortBy}
                    sortBy={sortBy}>
                    {products.data?.map((product, index) =>
                        product
                            ? (
                                    <ProductPreviewLong
                                        key={product.code}
                                        AddToCart={<AddToCartEshop lang={lang} product={product} type="long" />}
                                        Availability={<Availability className={css.availability} product={product} />}
                                        loadingState={!products.isSuccess}
                                        onClick={event => handleSendReport(event, { code: product.code, slug: product.detail.slug })}
                                        product={product}
                                        ProductPrice={!product.availability.isNotForSale && <PriceEshopPreview lang={lang} product={product} />}
                                        AdditionalInfo={(
                                            <>
                                                <Sticker product={product} />
                                                <ProductLabel product={product} showDiscount={false} />
                                                <ProductIcon product={product} />
                                                <ProductDiscount product={product} />
                                            </>
                                        )}
                                    />
                                )
                            : <ProductPreviewLong key={index} loadingState />,
                    )}
                </LayoutGrid>
            </div>
        </div>
    )
}
